import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { FilterSidebar } from '../components/listings/FilterSidebar';
import { ListingGrid } from '../components/listings/ListingGrid';
import { listingsAPI, dataAPI, savedListingsAPI, savedSearchesAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Search, SlidersHorizontal, Grid3X3, LayoutList, BookmarkPlus, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [makes, setMakes] = useState([]);
  const [states, setStates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [savedListings, setSavedListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [viewMode, setViewMode] = useState('grid');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [saveSearchDialogOpen, setSaveSearchDialogOpen] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(true);
  const [savingSearch, setSavingSearch] = useState(false);
  
  // Get filters from URL
  const getFiltersFromParams = useCallback(() => {
    const filters = {};
    const params = ['category', 'subcategory', 'make', 'model', 'condition', 'state', 'search', 'sort_by'];
    params.forEach(param => {
      const value = searchParams.get(param);
      if (value) filters[param] = value;
    });
    
    const numericParams = ['year_min', 'year_max', 'price_min', 'price_max', 'mileage_min', 'mileage_max'];
    numericParams.forEach(param => {
      const value = searchParams.get(param);
      if (value) filters[param] = parseInt(value);
    });
    
    if (searchParams.get('featured') === 'true') {
      filters.featured = true;
    }
    
    return filters;
  }, [searchParams]);

  const [filters, setFilters] = useState(getFiltersFromParams());

  // Re-sync filters whenever the URL search params change (e.g. user clicks a
  // category link in the top nav while already on /browse). We compare against
  // a serialized snapshot to avoid an infinite loop with the effect below that
  // pushes filters back into the URL.
  const lastParamsKey = useRef(searchParams.toString());
  useEffect(() => {
    const currentKey = searchParams.toString();
    if (currentKey === lastParamsKey.current) return;
    lastParamsKey.current = currentKey;
    setFilters(getFiltersFromParams());
    setSearchQuery(searchParams.get('search') || '');
  }, [searchParams, getFiltersFromParams]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [makesRes, statesRes, catsRes] = await Promise.all([
          dataAPI.getMakes(filters.category),
          dataAPI.getStates(),
          api.get('/categories'),
        ]);
        setMakes(makesRes.data);
        setStates(statesRes.data);
        setCategories(catsRes.data);
        
        if (isAuthenticated) {
          const savedRes = await savedListingsAPI.getAll();
          setSavedListings(savedRes.data.map(l => l.id));
        }
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };
    fetchData();
  }, [filters.category, isAuthenticated]);

  // Fetch listings when filters change
  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const params = { ...filters };
        if (searchQuery) params.search = searchQuery;
        
        const response = await listingsAPI.getAll(params);
        setListings(response.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
        toast.error('No se pudieron cargar los anuncios');
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [filters, searchQuery]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.set(key, value.toString());
      }
    });
    if (searchQuery) params.set('search', searchQuery);
    setSearchParams(params, { replace: true });
  }, [filters, searchQuery, setSearchParams]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // The search is already reactive via the useEffect
  };

  const handleSaveListing = async (listingId) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar anuncios');
      return;
    }
    
    try {
      if (savedListings.includes(listingId)) {
        await savedListingsAPI.unsave(listingId);
        setSavedListings(prev => prev.filter(id => id !== listingId));
        toast.success('Anuncio eliminado de guardados');
      } else {
        await savedListingsAPI.save(listingId);
        setSavedListings(prev => [...prev, listingId]);
        toast.success('Anuncio guardado');
      }
    } catch (error) {
      toast.error('No se pudo guardar el anuncio');
    }
  };

  const hasActiveFilters = Object.keys(filters).filter(k => k !== 'sort_by').length > 0 || searchQuery;

  const handleSaveSearch = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar búsquedas');
      return;
    }
    
    if (!hasActiveFilters) {
      toast.error('Aplica algunos filtros antes de guardar la búsqueda');
      return;
    }
    
    setSavingSearch(true);
    try {
      const searchFilters = { ...filters };
      if (searchQuery) searchFilters.search = searchQuery;
      
      await savedSearchesAPI.create({
        filters_json: searchFilters,
        alert_enabled: alertEnabled,
        name: saveSearchName || generateSearchName(searchFilters)
      });
      
      toast.success(alertEnabled 
        ? 'Search saved! You\'ll be notified of new matches.' 
        : 'Search saved successfully!');
      setSaveSearchDialogOpen(false);
      setSaveSearchName('');
      setAlertEnabled(true);
    } catch (error) {
      toast.error('No se pudo guardar la búsqueda');
    } finally {
      setSavingSearch(false);
    }
  };

  const generateSearchName = (searchFilters) => {
    const parts = [];
    if (searchFilters.category) parts.push(searchFilters.category);
    if (searchFilters.make) parts.push(searchFilters.make);
    if (searchFilters.model) parts.push(searchFilters.model);
    if (searchFilters.search) parts.push(`"${searchFilters.search}"`);
    if (searchFilters.price_max) parts.push(`under $${searchFilters.price_max.toLocaleString()}`);
    if (searchFilters.year_min) parts.push(`${searchFilters.year_min}+`);
    if (searchFilters.state) parts.push(`in ${searchFilters.state}`);
    return parts.length > 0 ? parts.join(' ') : 'My Search';
  };

  const sortOptions = [
    { value: 'newest', label: 'Más recientes primero' },
    { value: 'oldest', label: 'Más antiguos primero' },
    { value: 'price_low', label: 'Precio: menor a mayor' },
    { value: 'price_high', label: 'Precio: mayor a menor' },
    { value: 'year_new', label: 'Año: más nuevo' },
    { value: 'year_old', label: 'Año: más antiguo' },
  ];

  return (
    <div className="min-h-screen bg-secondary" data-testid="browse-page">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-16 z-40">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-xl w-full">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar anuncios..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 pl-12 pr-4 bg-secondary border-0 rounded-sm"
                  data-testid="browse-search-input"
                />
              </div>
            </form>

            {/* Controls */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              {/* Mobile Filter Button */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="outline" className="flex-1 md:flex-none" data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="h-4 w-4 mr-2" />Filtros</Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 p-0">
                  <div className="p-4 overflow-y-auto h-full">
                    <FilterSidebar
                      filters={filters}
                      onChange={handleFilterChange}
                      onReset={handleResetFilters}
                      makes={makes}
                      states={states}
                      categories={categories}
                    />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Sort */}
              <Select
                value={filters.sort_by || 'newest'}
                onValueChange={(value) => handleFilterChange({ sort_by: value })}
              >
                <SelectTrigger className="w-[180px]" data-testid="sort-select">
                  <SelectValue placeholder="Ordenar por" />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* View Mode */}
              <div className="hidden md:flex border border-border rounded-sm">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="rounded-none rounded-l-sm"
                  onClick={() => setViewMode('grid')}
                  data-testid="grid-view-btn"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="rounded-none rounded-r-sm"
                  onClick={() => setViewMode('list')}
                  data-testid="list-view-btn"
                >
                  <LayoutList className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-muted-foreground" data-testid="results-count">
              {listings.length} anuncio{listings.length === 1 ? '' : 's'} encontrado{listings.length === 1 ? '' : 's'}
            </span>
            {isAuthenticated && hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSaveSearchDialogOpen(true)}
                className="text-accent border-accent hover:bg-accent/10"
                data-testid="save-search-btn"
              >
                <BookmarkPlus className="h-4 w-4 mr-2" />Guardar búsqueda</Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-36">
              <FilterSidebar
                filters={filters}
                onChange={handleFilterChange}
                onReset={handleResetFilters}
                makes={makes}
                states={states}
                categories={categories}
              />
            </div>
          </aside>

          {/* Listings */}
          <main className="flex-1 min-w-0">
            <ListingGrid
              listings={listings}
              loading={loading}
              savedListings={savedListings}
              onSave={handleSaveListing}
              columns={viewMode === 'list' ? 2 : 3}
              emptyMessage="Ningún anuncio coincide con tus criterios. Intenta ajustar los filtros."
            />
          </main>
        </div>
      </div>

      {/* Save Search Dialog */}
      <Dialog open={saveSearchDialogOpen} onOpenChange={setSaveSearchDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="save-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Guardar búsqueda</DialogTitle>
            <DialogDescription>Guarda tus criterios de búsqueda para acceder a ellos rápidamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="search-name" className="text-sm font-semibold uppercase tracking-wide">Nombre de la búsqueda (opcional)</Label>
              <Input
                id="search-name"
                placeholder={generateSearchName(filters)}
                value={saveSearchName}
                onChange={(e) => setSaveSearchName(e.target.value)}
                data-testid="search-name-input"
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-secondary rounded-sm">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  {alertEnabled ? (
                    <Bell className="h-4 w-4 text-accent" />
                  ) : (
                    <BellOff className="h-4 w-4 text-muted-foreground" />
                  )}
                  <Label htmlFor="alert-toggle" className="font-semibold cursor-pointer">Alertas por correo</Label>
                </div>
                <p className="text-sm text-muted-foreground">Recibe avisos cuando aparezcan nuevos anuncios que coincidan con tu búsqueda</p>
              </div>
              <Switch
                id="alert-toggle"
                checked={alertEnabled}
                onCheckedChange={setAlertEnabled}
                data-testid="alert-toggle"
              />
            </div>
            <div className="p-3 bg-muted rounded-sm">
              <p className="text-sm font-semibold mb-2">Filtros actuales:</p>
              <div className="text-sm text-muted-foreground space-y-1">
                {searchQuery && <p>Búsqueda: &ldquo;{searchQuery}&rdquo;</p>}
                {filters.category && <p>Category: {filters.category}</p>}
                {filters.make && <p>Make: {filters.make}</p>}
                {filters.model && <p>Model: {filters.model}</p>}
                {filters.year_min && <p>Year (min): {filters.year_min}</p>}
                {filters.year_max && <p>Year (max): {filters.year_max}</p>}
                {filters.price_min && <p>Price (min): ${filters.price_min.toLocaleString()}</p>}
                {filters.price_max && <p>Price (max): ${filters.price_max.toLocaleString()}</p>}
                {filters.condition && <p>Condition: {filters.condition}</p>}
                {filters.state && <p>State: {filters.state}</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveSearchDialogOpen(false)}
            >Cancelar</Button>
            <Button
              className="btn-accent"
              onClick={handleSaveSearch}
              disabled={savingSearch}
              data-testid="confirm-save-search-btn"
            >
              {savingSearch ? 'Guardando...' : 'Save Search'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
